import * as C from "../../../../../../ui/common/CommonStyles";
import { IconCalendar, IconPhotoPlus, IconX } from "@tabler/icons-react";
import {
  noticeTypeOptions,
  solutionOptions,
} from "../../../../../../../shared/constants/commonCode";
import { DateInput } from "@mantine/dates";
import { Controller } from "react-hook-form";
import Swal from "sweetalert2";
import * as M from "@mantine/core";
import usePopupForm from "../hooks/usePopupForm";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { PopupFormValues, PopupPreviewInfo } from "../types";
import { useUploadPopupImages } from "../hooks/useUploadPopupImages";
import { useNavigate, useParams } from "react-router-dom";
import { validateFileSize } from "../utils/validateFileSize";
import usePostPopup from "../hooks/usePostPopup";
import { queryClient } from "../../../../../../../shared/queryClient";
import { showSuccessNotification } from "../../../../../../ui/notification";
import { AxiosError } from "axios";
import { handleError } from "../../../../../../ui/err/handleError";
import { formatDateToUTCString } from "../utils/formatDate";
import { usePopupItem } from "../hooks/usePopupItem";
import { useEditPopup } from "../hooks/useEditPopup";
import { runtimeConfig } from "../../../../../../../shared/config/runtimeConfig";

const cloudFrontUrl = runtimeConfig.s3BucketUrl;

export default function PopupForm(props: {
  open: () => void;
  setPreviewInfo: Dispatch<SetStateAction<PopupPreviewInfo | null>>;
}) {
  const { open, setPreviewInfo } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const { form, file, handleFile } = usePopupForm();
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const {
    getValues,
    register,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;
  const { postPopup } = usePostPopup();
  const { editPopup } = useEditPopup();
  const { uploadPopupImage } = useUploadPopupImages();
  const { popupItem } = usePopupItem({
    queryKey: ["getPopupItem", id || ""],
    payload: { popupId: Number(id) },
    enabled: !!id,
  });

  const onCancel = () => {
    Swal.fire({
      title: "등록을 취소하시겠습니까?",
      text: "작성 중인 내용은 저장되지 않습니다.",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "확인",
      cancelButtonText: "취소",
    }).then((result) => {
      if (result.isConfirmed) {
        reset();
        onRemoveImage();
        navigate(-1);
      }
    });
  };

  const handlePreview = () => {
    open();
    const values = getValues();
    setPreviewInfo({ ...values, imageSrc: previewSrc });
  };

  const onRemoveImage = () => {
    if (previewSrc) {
      setPreviewSrc(null);
      handleFile(null);
    }
  };

  const handleChange = async (value: File | null) => {
    if (!value) {
      onRemoveImage();
      return;
    }

    if (!validateFileSize(value.size)) {
      Swal.fire({
        icon: "error",
        title: "파일 용량 초과",
        text: "업로드 가능한 파일 용량은 2MB 이하입니다.",
      });
      return;
    }
    handleFile(value);
    const formData = new FormData();
    formData.append("file", value);
    const result = await uploadPopupImage(formData);
    const previewSrc = result.data.result[0];
    setPreviewSrc(previewSrc);
    setValue("imgPosition", "top");
  };

  const onSubmit = (data: PopupFormValues) => {
    const utcStartDate = formatDateToUTCString(data.startDate!, false);
    const utcEndDate = formatDateToUTCString(data.endDate!, true);
    const submitValues = {
      ...data,
      startDate: utcStartDate,
      endDate: utcEndDate,
      showYn: data.showYn === "Y" ? true : false,
      images: previewSrc ? [previewSrc] : [],
    };

    if (id) {
      editPopup(
        {
          popupId: Number(id),
          data: submitValues,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["popupList"] });
            showSuccessNotification("변경 되었습니다.");
            navigate("/admin/edgecross/notice");
          },
          onError: (error: unknown) => {
            if (error instanceof AxiosError) {
              handleError(error);
            }
          },
        }
      );
    } else {
      postPopup(submitValues, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["popupList"] });
          showSuccessNotification("등록 되었습니다.");
          navigate("/admin/edgecross/notice");
        },
        onError: (error: unknown) => {
          if (error instanceof AxiosError) {
            handleError(error);
          }
        },
      });
    }
  };

  useEffect(() => {
    if (id && popupItem) {
      reset({
        title: popupItem.title,
        content: popupItem.content,
        noticeCd: popupItem.noticeCd,
        solutionCd: popupItem.solutionCd,
        newTabYn: popupItem.newTabYn,
        startDate: new Date(`${popupItem.startDate}Z`),
        endDate: new Date(`${popupItem.endDate}Z`),
        showYn: popupItem.showYn ? "Y" : "N",
        imgPosition: popupItem.imgPosition || "",
        url: popupItem.url || null,
      });

      if (popupItem.images && popupItem.images.length > 0) {
        setPreviewSrc(popupItem.images[0]);
      }
    }
  }, [id, popupItem]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <C.ListBox>
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            팝업 기간
            <M.Text component="span" c="red">
              {" "}
              *
            </M.Text>
          </M.Text>
          <M.Flex align="center" gap="xs">
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...field}
                  leftSection={<IconCalendar size={16} />}
                  placeholder="2025-06-27"
                  valueFormat="YYYY-MM-DD"
                  error={errors.startDate?.message}
                />
              )}
            />
            -
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...field}
                  leftSection={<IconCalendar size={16} />}
                  placeholder="2025-06-27"
                  valueFormat="YYYY-MM-DD"
                  error={errors.endDate?.message}
                />
              )}
            />
          </M.Flex>
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            노출 상태
            <M.Text component="span" c="red">
              {" "}
              *
            </M.Text>
          </M.Text>
          <Controller
            name="showYn"
            control={control}
            render={({ field }) => (
              <M.Radio.Group {...field}>
                <M.Flex gap="md" mt={8}>
                  <M.Radio value="Y" label="표시" />
                  <M.Radio value="N" label="숨김" />
                </M.Flex>
              </M.Radio.Group>
            )}
          />
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex gap="md">
          <M.Flex style={{ flex: 1 }}>
            <M.Text miw="10rem" fw="bold" size="md">
              서비스 채널
              <M.Text component="span" c="red">
                {" "}
                *
              </M.Text>
            </M.Text>
            <Controller
              name="solutionCd"
              control={control}
              render={({ field }) => (
                <M.Select
                  data={solutionOptions}
                  error={errors.solutionCd?.message}
                  {...field}
                />
              )}
            />
          </M.Flex>
          <M.Flex style={{ flex: 1 }}>
            <M.Text miw="10rem" fw="bold" size="md">
              팝업 유형
              <M.Text component="span" c="red">
                {" "}
                *
              </M.Text>
            </M.Text>
            <Controller
              name="noticeCd"
              control={control}
              render={({ field }) => (
                <M.Select
                  error={errors.noticeCd?.message}
                  data={noticeTypeOptions}
                  {...field}
                />
              )}
            />
          </M.Flex>
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            제목
            <M.Text component="span" c="red">
              {" "}
              *
            </M.Text>
          </M.Text>
          <M.TextInput
            style={{ flex: 1 }}
            placeholder="제목을 입력해주세요."
            error={errors.title?.message}
            {...register("title")}
          />
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            내용
            <M.Text component="span" c="red">
              {" "}
              *
            </M.Text>
          </M.Text>
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <M.Textarea
                style={{ flex: 1 }}
                autosize
                minRows={6}
                maxRows={6}
                placeholder="내용을 입력해주세요."
                error={errors.content?.message}
                {...field}
              />
            )}
          />
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            이미지
          </M.Text>
          <M.Box>
            <M.FileInput
              miw="20rem"
              accept="image/png,image/jpeg,image/jpg"
              placeholder={"파일을 첨부하세요."}
              rightSection={<IconPhotoPlus size={16} />}
              value={file}
              onChange={(file) => {
                handleChange(file);
              }}
            />
            <M.Text size="xs" c="primary" my={4}>
              <M.Text component="span">지원 형식 : PNG, JPG, JPEG 파일</M.Text>
              <M.Text component="span" ml="xl">
                업로드 용량 : 2MB 이하
              </M.Text>
            </M.Text>
            {previewSrc && (
              <M.Box
                pos="relative"
                w="100%"
                maw={260}
                mt="sm"
                style={{
                  border: "1px solid #ced4da",
                  padding: 4,
                }}
              >
                <>
                  <M.Image
                    src={cloudFrontUrl + "/" + previewSrc}
                    alt="미리보기"
                    w="100%"
                    h="auto"
                    mah={260}
                    fit="contain"
                  />
                  <M.ActionIcon
                    size="xs"
                    aria-label="이미지 삭제"
                    style={{ position: "absolute", top: 8, right: 8 }}
                    radius="xl"
                    onClick={onRemoveImage}
                  >
                    <IconX size={14} />
                  </M.ActionIcon>
                </>
              </M.Box>
            )}
          </M.Box>
        </M.Flex>
        {previewSrc && (
          <>
            <M.Space h="md" />
            <M.Flex>
              <M.Text miw="10rem" fw="bold" size="md">
                위치
                <M.Text component="span" c="red">
                  {" "}
                  *
                </M.Text>
              </M.Text>
              <Controller
                name="imgPosition"
                control={control}
                render={({ field }) => (
                  <M.Radio.Group error={errors.imgPosition?.message} {...field}>
                    <M.Flex gap="md" mt={8}>
                      <M.Radio value="top" label="텍스트 영역 상단" />
                      <M.Radio value="bottom" label="텍스트 영역 하단" />
                    </M.Flex>
                  </M.Radio.Group>
                )}
              />
            </M.Flex>
          </>
        )}
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            페이지 이동 URL
          </M.Text>
          <M.Box style={{ flex: 1 }}>
            <M.TextInput
              w="100%"
              placeholder="이동할 페이지 URL을 입력해주세요."
              error={errors.url?.message}
              {...register("url")}
            />
            <Controller
              name="newTabYn"
              control={control}
              render={({ field }) => (
                <M.Checkbox
                  label="URL 새창 열림"
                  mt="md"
                  size="sm"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                />
              )}
            />
          </M.Box>
        </M.Flex>
        <M.Divider my="md" />
        <M.Flex>
          <M.Text miw="10rem" fw="bold" size="md">
            미리보기
          </M.Text>
          <M.Button variant="outline" onClick={handlePreview}>
            미리보기
          </M.Button>
        </M.Flex>
        <M.Group justify="center" mt="xl">
          <M.Button variant="outline" type="button" onClick={onCancel}>
            취소
          </M.Button>
          <M.Button color="secondary" type="submit">
            저장
          </M.Button>
        </M.Group>
      </C.ListBox>
    </form>
  );
}
